import { httpGet } from "../../../../../Utils/apis/apis";

const API_BASE = "/ExposureGroup";

const formatDate = (isoString) => {
  if (!isoString) return "-";
  return new Date(isoString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const deduplicateById = (arr) => {
  const seen = new Set();
  return (arr || []).filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const normalizeGroup = (raw) => {
  const borrowers = deduplicateById(raw.borrowers);
  const loans = deduplicateById(raw.loans);

  return {
    ...raw,
    id: raw.id || raw.Id,
    name: raw.name,
    totalExposure: Number(raw.totalExposure) || 0,
    htbApproved: Number(raw.htbApproved ?? raw.carsApproved) || 0,
    createdBy: raw.CreatedBy || raw.createdBy || "-",
    createdAt: formatDate(raw.createdAt),
    borrowers: borrowers.length > 0 ? borrowers : (raw.borrowerCount ? Array(Number(raw.borrowerCount)).fill({}) : []),
    loans: loans.length > 0 ? loans : (raw.loanCount ? Array(Number(raw.loanCount)).fill({}) : []),
  };
};

export const fetchExposureGroups = async () => {
  const data = await httpGet(`${API_BASE}/getall`);
  if (data instanceof Error) throw data;
  return Array.isArray(data) ? data.map(normalizeGroup) : [];
};

export const fetchExposureGroupById = async (groupId) => {
  const data = await httpGet(`${API_BASE}/getgroupbyid/${groupId}`);
  if (data instanceof Error) throw data;
  return normalizeGroup(data);
};

// Cubic bezier SVG path between two points with horizontal control handles
export const bezier = (a, b) => {
  const dx = Math.max(40, (b.x - a.x) * 0.5);
  const c1 = { x: a.x + dx, y: a.y };
  const c2 = { x: b.x - dx, y: b.y };
  return `M ${a.x} ${a.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
};

export const formatAmount = (n, opts = {}) => {
  if (n == null || isNaN(n)) return "-";
  const { decimals = 0 } = opts;
  return Number(n).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatEUR = (n, opts = {}) => {
  if (n == null || isNaN(n)) return "-";
  if (typeof window !== "undefined" && typeof window.FORMAT_GBP === "function") {
    return window.FORMAT_GBP(n, opts);
  }
  const { decimals = 0, compact = false } = opts;
  if (compact && Math.abs(n) >= 1_000_000) {
    return "£" + (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "m";
  }
  if (compact && Math.abs(n) >= 1_000) {
    return "£" + (n / 1_000).toFixed(0) + "k";
  }
  return "£" + n.toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const getGroupStats = (group) => {
  if (!group) return { borrowers: 0, loans: 0 };
  const borrowersList = group.borrowers || [];
  const loansList = group.loans || [];

  return {
    borrowers: borrowersList.length,
    loans: loansList.length,
  };
};

// A4: total outstanding balance across all loans in the group
export const getOtherOpenLoansSum = (loans) => {
  if (!loans) return 0;
  return loans.reduce((sum, l) => sum + (Number(l.totalExposure) || 0), 0);
};

// A5: total CARS-approved amount across all loans in the group
export const getOtherCarsApprovedSum = (loans) => {
  if (!loans) return 0;
  return loans.reduce((sum, l) => sum + (Number(l.htbApproved ?? l.carsApproved) || 0), 0);
};
